import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { ROLE_LABELS, ROLES } from "@/lib/constants";
import { toast } from "sonner";

export default function StaffPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (roleFilter !== "all") params.set("role", roleFilter);
      const { data } = await api.get(`/users?${params.toString()}`);
      setUsers(data.users || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, roleFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ active: true, role: "staff" });
    setDrawerOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...u, password: "" });
    setDrawerOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password || !form.name || !form.role) {
          toast.error("Email, password, name & role are required");
          return;
        }
        await api.post("/users", form);
        toast.success("User created");
      }
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="staff-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Administration</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Staff Directory</h1>
          <p className="text-sm text-slate-500 mt-1">{users.length} users</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="staff-create-button">
          <Plus className="h-4 w-4 mr-2" /> Add Staff
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by name, email, department..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="staff-search-input" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-52" data-testid="staff-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Name", "Email", "Role", "Designation", "Department", "Status", ""].map((h) => (
                  <th key={h} className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">Loading...</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">No users found</td></tr>
              )}
              {!loading && users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 font-medium text-slate-900">{u.name}</td>
                  <td className="py-3 px-4 text-slate-600">{u.email}</td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">{ROLE_LABELS[u.role] || u.role}</Badge>
                  </td>
                  <td className="py-3 px-4 text-slate-700">{u.designation || "—"}</td>
                  <td className="py-3 px-4 text-slate-700">{u.department || "—"}</td>
                  <td className="py-3 px-4">
                    {u.active ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
                    )}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4 text-slate-700" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete user?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently remove this user.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit User" : "Add New User"}</SheetTitle>
            <SheetDescription>{editing ? "Update user info, role and access." : "Create a new staff account."}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <Field label="Full Name *">
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
            </Field>
            <Field label="Email *">
              <Input type="email" disabled={!!editing} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" />
            </Field>
            <Field label={editing ? "New Password (leave blank to keep)" : "Password *"}>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" />
            </Field>
            <Field label="Role *">
              <Select value={form.role || ""} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Designation">
              <Input value={form.designation || ""} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </Field>
            <Field label="Department">
              <Input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </Field>
            <Field label="Phone">
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Joining Date">
              <Input type="date" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
            </Field>
            <Field label="Reporting Manager">
              <Input value={form.reporting_manager || ""} onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })} />
            </Field>
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-md">
              <Label className="text-slate-700">Active</Label>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="staff-active-switch" />
            </div>
            <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="staff-save-button">{editing ? "Save" : "Create"}</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
